import { useEffect, useRef, useState } from "react";

/**
 * A draw-your-signature canvas (mouse, touch, and pen). Exposes the drawing as a PNG
 * data URL via `onChange`, matching the client's own waiver's signature capture.
 */
export function SignatureCanvas({ onChange, disabled = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#14161a";
  }, []);

  const pointFromEvent = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event) => {
    if (disabled) return;
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawing.current = true;
    lastPoint.current = pointFromEvent(event);
  };
  const move = (event) => {
    if (!drawing.current || disabled) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const point = pointFromEvent(event);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPoint.current = point;
    if (!hasSignature) setHasSignature(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasSignature) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange("");
  };

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        role="img"
        aria-label={hasSignature ? "Drawn signature" : "Empty signature pad — draw your signature here"}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      {!hasSignature && <span className="signature-pad-placeholder" aria-hidden="true">Draw your signature</span>}
      <div className="signature-pad-actions">
        <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={clear} disabled={disabled || !hasSignature}>Clear signature</button>
      </div>
    </div>
  );
}
